import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../services/api';
import {
  Palette,
  Video,
  Crown,
  Briefcase,
  Globe,
  Music,
  FileText,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Calendar,
  ArrowLeft,
  Sparkles,
  Loader2,
  CreditCard,
  Users,
  Shield,
  TrendingUp,
  BookOpen,
  Building
} from 'lucide-react';

// ATHLETE Electives - Spring '26 (Performer View)
const ATHLETE_ELECTIVES = [
  {
    id: 'branding-principles',
    title: 'Branding Principles',
    icon: Palette,
    color: 'orange',
    deposit: 25,
    synopsis: 'Beyond a logo and a color palette, your brand is your reputation in the marketplace. This elective teaches the science of visual identity, storytelling, and consistency. Learn how to communicate your "Pillars of Identity" to fans, scouts, and corporate partners to build a brand that lasts longer than your playing days.',
    highlights: ['Visual Identity Design', 'Brand Storytelling', 'Corporate Partnership Ready']
  },
  {
    id: 'casting-preparedness',
    title: 'Casting Preparedness',
    icon: Video,
    color: 'blue',
    deposit: 25,
    synopsis: 'Every interview is an audition. This module prepares athletes for the camera, from commercial scripts to post-game press conferences. We focus on vocal tonality, body language, and "message control," ensuring that when the spotlight hits, you are prepared to deliver a performance that increases your market value.',
    highlights: ['Camera Presence', 'Message Control', 'Media Interview Training']
  },
  {
    id: 'strategic-thinking',
    title: 'Strategic Thinking',
    icon: Crown,
    color: 'purple',
    deposit: 25,
    synopsis: 'Masters of the game don\'t just react; they anticipate. This course develops advanced deductive reasoning and foresight. Scholar-Athletes learn to recognize patterns on the field and in the boardroom three moves before they happen.',
    highlights: ['Pattern Recognition', 'Strategic Foresight', 'Tactical Decision Making']
  },
  {
    id: 'entrepreneurship',
    title: 'Entrepreneurship',
    icon: Briefcase,
    color: 'green',
    deposit: 25,
    synopsis: 'Transition from being a "worker" to being an "owner." This elective covers the fundamentals of the "Business of You," including scalable models, revenue streams, and risk management. We teach you how to identify market gaps and launch ventures that leverage your athletic platform into a business empire.',
    highlights: ['Business Fundamentals', 'Revenue Streams', 'Platform Leverage']
  },
  {
    id: 'fiba-languages',
    title: 'FIBA Languages',
    icon: Globe,
    color: 'teal',
    deposit: 25,
    synopsis: 'The game is global. For athletes eyeing professional opportunities in Europe, Asia, or South America, this course provides introductory linguistics and cultural IQ. Focus on "On-Court Communication" and "Contract Fluency" in key international languages to ensure you are never "lost in translation" overseas.',
    highlights: ['Court Communication', 'Contract Fluency', 'Cultural Intelligence']
  },
  {
    id: 'music-production',
    title: 'Music Production',
    icon: Music,
    color: 'pink',
    deposit: 25,
    synopsis: 'Explore the technical architecture of the audio industry. This elective covers the basics of digital audio workstations (DAW), rhythm theory, and the business of publishing. Perfect for the creative athlete looking to master the tools of the studio and understand the monetization of sound and media.',
    highlights: ['DAW Fundamentals', 'Rhythm Theory', 'Music Business']
  },
  {
    id: 'nil-deep-dive',
    title: 'My Name Image and Likeness (NIL)',
    icon: FileText,
    color: 'gold',
    deposit: 25,
    synopsis: 'This is the definitive playbook for personal monetization. We move past the hype to look at the legalities, contract structures, and long-term equity. Learn how to evaluate a deal, manage your "Likeness" assets, and protect your earnings while building a sustainable financial future.',
    highlights: ['Contract Analysis', 'Asset Management', 'Long-term Equity']
  },
  {
    id: 'reclassifying',
    title: 'Reclassifying & What to Know',
    icon: GraduationCap,
    color: 'red',
    deposit: 25,
    synopsis: 'Timing is a strategic weapon. This module explores the academic, athletic, and psychological implications of reclassification. We break down the "Year of Development" logic, CIF / high-school eligibility impacts, and how to use an extra year to maximize both your physical "Combine" stats and your Academic Power Score.',
    highlights: ['CIF Eligibility', 'Strategic Timing', 'Development Planning']
  }
];

// PARENT Executive Electives - Spring '26 (Executive View)
const PARENT_ELECTIVES = [
  {
    id: 'executive-media-governance',
    title: 'Executive Media Governance',
    icon: Shield,
    color: 'purple',
    deposit: 25,
    synopsis: 'Learn to manage the athlete\'s media ecosystem, vet interview requests, and coach soundbite logic to increase brand value while protecting privacy. This executive module teaches parents how to serve as the "Chief Communications Officer" of their athlete\'s brand, ensuring every public appearance is strategic and protected.',
    highlights: ['Media Ecosystem Management', 'Interview Vetting', 'Privacy Protection']
  },
  {
    id: 'legacy-portfolio-management',
    title: 'Legacy Portfolio Management',
    icon: TrendingUp,
    color: 'slate',
    deposit: 25,
    synopsis: 'Shift from social media to asset building. Create a family mission statement that ensures the athlete\'s public presence aligns with 40-year career goals. This module teaches executive-level portfolio thinking—treating your athlete\'s brand not as a moment, but as a multi-generational legacy.',
    highlights: ['Asset Building', 'Family Mission Statement', '40-Year Career Planning']
  },
  {
    id: 'nil-advocacy-contract-literacy',
    title: 'NIL Advocacy & Contract Literacy',
    icon: FileText,
    color: 'gold',
    deposit: 25,
    synopsis: 'The "Executive Intelligence" module. Equip parents to audit NIL offers, identify predatory language, and negotiate from a position of power. Learn the red flags, the leverage points, and the legal frameworks that separate amateur parents from professional advocates.',
    highlights: ['Contract Auditing', 'Predatory Language Detection', 'Negotiation Power']
  },
  {
    id: 'tactical-logic-family-playbooks',
    title: 'Tactical Logic & Family Strategic Playbooks',
    icon: Crown,
    color: 'blue',
    deposit: 25,
    synopsis: 'Master strategic frameworks to manage recruitment moves, anticipate collegiate options, and facilitate high-level situational debriefs. This module transforms parents into strategic advisors who can see the recruitment landscape three moves ahead.',
    highlights: ['Strategic Frameworks', 'Recruitment Strategy', 'Situational Analysis']
  },
  {
    id: 'family-office-entrepreneurship',
    title: 'The Family Office: Sports Entrepreneurship',
    icon: Building,
    color: 'green',
    deposit: 25,
    synopsis: 'Treat the athlete\'s career as a startup. Learn resource allocation, LLC/Foundation structure, and building generational wealth. This executive track teaches parents how to build the infrastructure around their athlete that transforms short-term earnings into long-term family prosperity.',
    highlights: ['LLC & Foundation Structure', 'Resource Allocation', 'Generational Wealth']
  }
];

const getColorClasses = (color) => {
  const colors = {
    orange: { bg: 'bg-orange-500/20', text: 'text-orange-500', border: 'border-orange-500/50', hover: 'hover:border-orange-500', glow: 'shadow-orange-500/20' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-500', border: 'border-blue-500/50', hover: 'hover:border-blue-500', glow: 'shadow-blue-500/20' },
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-500', border: 'border-purple-500/50', hover: 'hover:border-purple-500', glow: 'shadow-purple-500/20' },
    green: { bg: 'bg-green-500/20', text: 'text-green-500', border: 'border-green-500/50', hover: 'hover:border-green-500', glow: 'shadow-green-500/20' },
    teal: { bg: 'bg-teal-500/20', text: 'text-teal-500', border: 'border-teal-500/50', hover: 'hover:border-teal-500', glow: 'shadow-teal-500/20' },
    pink: { bg: 'bg-pink-500/20', text: 'text-pink-500', border: 'border-pink-500/50', hover: 'hover:border-pink-500', glow: 'shadow-pink-500/20' },
    gold: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', border: 'border-yellow-500/50', hover: 'hover:border-yellow-500', glow: 'shadow-yellow-500/20' },
    red: { bg: 'bg-red-500/20', text: 'text-red-500', border: 'border-red-500/50', hover: 'hover:border-red-500', glow: 'shadow-red-500/20' },
    slate: { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/50', hover: 'hover:border-slate-400', glow: 'shadow-slate-500/20' }
  };
  return colors[color] || colors.orange;
};

const ElectiveCard = ({ elective, onReserve, isLoading, loadingId, isParentView }) => {
  const [expanded, setExpanded] = useState(false);
  const colors = getColorClasses(elective.color);
  const Icon = elective.icon;
  const isCurrentlyLoading = isLoading && loadingId === elective.id;

  return (
    <Card className={`${isParentView ? 'bg-slate-900/80' : 'bg-gray-900'} border-2 ${isParentView ? 'border-slate-700' : 'border-gray-800'} ${colors.hover} transition-all duration-300 hover:shadow-lg ${colors.glow}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${colors.text}`} />
            </div>
            <div>
              <CardTitle className="text-white text-lg">{elective.title}</CardTitle>
              <Badge className={`${colors.bg} ${colors.text} border ${colors.border} mt-1`}>
                <Calendar className="w-3 h-3 mr-1" />
                Launch: Spring '26
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Preview Highlights */}
        <div className="flex flex-wrap gap-2">
          {elective.highlights.map((highlight, idx) => (
            <span key={idx} className={`px-2 py-1 ${isParentView ? 'bg-slate-800' : 'bg-gray-800'} text-gray-400 rounded text-xs`}>
              {highlight}
            </span>
          ))}
        </div>

        {/* Expand Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors w-full justify-center py-2 border-t ${isParentView ? 'border-slate-700' : 'border-gray-800'}`}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide Synopsis
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Read Full Synopsis
            </>
          )}
        </button>

        {/* Expanded Synopsis */}
        {expanded && (
          <div className={`${isParentView ? 'bg-slate-800/50' : 'bg-gray-800/50'} rounded-lg p-4 border-l-4 ${colors.border}`}>
            <p className="text-gray-300 text-sm leading-relaxed">
              {elective.synopsis}
            </p>
          </div>
        )}

        {/* Reserve Button */}
        <div className={`flex items-center justify-between pt-4 border-t ${isParentView ? 'border-slate-700' : 'border-gray-800'}`}>
          <div>
            <p className="text-2xl font-black text-white">${elective.deposit}</p>
            <p className="text-xs text-gray-500">Deposit to reserve</p>
          </div>
          <Button
            onClick={() => onReserve(elective)}
            disabled={isLoading}
            className={`${isParentView ? 'bg-purple-500/20 text-purple-400 border-purple-500/50 hover:bg-purple-500/30' : `${colors.bg} ${colors.text} border ${colors.border} hover:bg-opacity-30`} font-bold transition-all`}
            data-testid={`reserve-${elective.id}-btn`}
          >
            {isCurrentlyLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Reserve My Seat
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Electives = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  
  // Perspective Toggle State - check URL param first
  const [isParentView, setIsParentView] = useState(searchParams.get('view') === 'parent');

  // Update URL when view changes
  useEffect(() => {
    if (isParentView) {
      setSearchParams({ view: 'parent' });
    } else {
      setSearchParams({});
    }
  }, [isParentView, setSearchParams]);

  const handleReserve = async (elective) => {
    setIsLoading(true);
    setLoadingId(elective.id);

    try {
      const originUrl = window.location.origin;

      const response = await api.post('/academy/electives/checkout', {
        elective_id: elective.id,
        elective_title: elective.title,
        deposit_amount: elective.deposit,
        origin_url: originUrl,
        is_parent_track: isParentView
      });

      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(response.data.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(
        error.response?.data?.detail || 
        error.message || 
        'Failed to initiate checkout. Please try again.'
      );
      setIsLoading(false);
      setLoadingId(null);
    }
  };

  const currentElectives = isParentView ? PARENT_ELECTIVES : ATHLETE_ELECTIVES;

  return (
    <div className="min-h-screen bg-black pt-8">
      {/* Dynamic Background Grid */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: isParentView 
            ? `linear-gradient(rgba(100,116,139,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(100,116,139,0.15) 1px, transparent 1px)`
            : `linear-gradient(rgba(249,115,22,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black"></div>
      </div>

      {/* Header */}
      <div className={`relative z-10 ${isParentView ? 'bg-gradient-to-r from-purple-500/10 to-slate-500/10' : 'bg-gradient-to-r from-orange-500/10 to-purple-500/5'} border-b ${isParentView ? 'border-slate-700' : 'border-gray-800'}`}>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/academy')}
              className="text-gray-400 hover:text-white"
              data-testid="back-to-academy-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className={`w-14 h-14 ${isParentView ? 'bg-purple-500/20' : 'bg-orange-500/20'} rounded-xl flex items-center justify-center`}>
              {isParentView ? (
                <Users className="w-7 h-7 text-purple-500" />
              ) : (
                <Sparkles className="w-7 h-7 text-orange-500" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-black text-white">
                {isParentView ? 'EXECUTIVE TRACKS' : 'ELECTIVES'}
              </h1>
              <p className="text-gray-400">
                {isParentView ? 'Spring \'26 Parent Executive Modules' : 'Spring \'26 Strategic Modules'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* ========================================
            PERSPECTIVE TOGGLE
            ======================================== */}
        <div className="flex justify-center mb-10">
          <div className={`inline-flex rounded-xl p-1 ${isParentView ? 'bg-slate-800/80' : 'bg-gray-800/80'} border ${isParentView ? 'border-slate-700' : 'border-gray-700'}`}>
            <button
              onClick={() => setIsParentView(false)}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all ${
                !isParentView 
                  ? 'bg-orange-500 text-black shadow-lg shadow-orange-500/30' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Athlete View
              </span>
            </button>
            <button
              onClick={() => setIsParentView(true)}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all ${
                isParentView 
                  ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Parent View
              </span>
            </button>
          </div>
        </div>

        {/* Intro Section */}
        <div className="text-center mb-12">
          <Badge className={`${isParentView ? 'bg-purple-500/20 text-purple-400 border-purple-500/50' : 'bg-orange-500/20 text-orange-500 border-orange-500/50'} mb-4`}>
            {isParentView ? 'EXECUTIVE DEVELOPMENT' : 'RESERVE YOUR SEAT'}
          </Badge>
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
            {isParentView ? (
              <>Elevate Your <span className="text-purple-500">Executive Impact</span></>
            ) : (
              <>Expand Your <span className="text-orange-500">Competitive Edge</span></>
            )}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {isParentView ? (
              <>Each executive track equips parents to serve as strategic advisors. Place a <span className="text-purple-400 font-bold">$49 deposit</span> to secure your seat in the Spring '26 cohort and transform from spectator to strategist.</>
            ) : (
              <>Each elective is designed as a "seat-reservation" launch. By placing a <span className="text-orange-500 font-bold">$49 deposit</span>, you secure your spot in exclusive Spring '26 courses. Checkout is powered by Stripe for secure payment.</>
            )}
          </p>
        </div>

        {/* Electives Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {currentElectives.map((elective) => (
            <ElectiveCard
              key={elective.id}
              elective={elective}
              onReserve={handleReserve}
              isLoading={isLoading}
              loadingId={loadingId}
              isParentView={isParentView}
            />
          ))}
        </div>

        {/* Bottom Info */}
        <div className="mt-12 text-center">
          <Card className={`${isParentView ? 'bg-slate-900/80 border-slate-700' : 'bg-gray-900 border-gray-800'} inline-block max-w-xl`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <CreditCard className={`w-5 h-5 ${isParentView ? 'text-purple-500' : 'text-orange-500'}`} />
                <span className="text-white font-bold">Secure Checkout</span>
              </div>
              <p className="text-gray-400 text-sm">
                {isParentView ? (
                  <><span className="text-purple-400 font-bold">Note:</span> Executive tracks appear in your "Family Office" dashboard once the deposit is confirmed. For every hour your athlete trains, there's a corresponding executive module for you.</>
                ) : (
                  <><span className="text-orange-500 font-bold">Note:</span> Reserved electives will appear in your "Minor Focus" section on your profile once the deposit is confirmed. All payments are processed securely via Stripe.</>
                )}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Dual Enrollment CTA for Parent View */}
        {isParentView && (
          <div className="mt-8 text-center">
            <div className="inline-block bg-gradient-to-r from-purple-500/10 to-orange-500/10 border border-purple-500/30 rounded-xl p-6">
              <p className="text-gray-300 text-sm mb-3">
                <span className="text-purple-400 font-bold">Strategist Tier Benefit:</span> Dual-enrollment program for the whole household
              </p>
              <Button
                onClick={() => navigate('/academy')}
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
              >
                Explore Full Academy Programs
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Electives;
